import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'
import {
  clearPunctualityDay,
  getPunctualityNotices,
  sendPunctualityDay,
  setPunctualityEmail
} from 'api/kekaAttendance'

// Centre Audit > Punctuality Mails (2 Nov 2026). One day of Keka at a time:
// who was late or absent, their nth late day this month and total deduction.
// Remove anyone wrong, then send. Rules live in the API.

const POLL_MS = 5000
const POLL_TIMES = 36

const STATUS_LABEL = {
  to_send: 'to send',
  sending: 'sending',
  sent: 'sent',
  failed: 'failed',
  cleared: 'removed'
}

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2), width: '100%' },
  bar: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  note: { marginBottom: theme.spacing(2) },
  error: { color: theme.palette.error.main, marginBottom: theme.spacing(2) },
  faded: { opacity: 0.5 }
}))

const markText = (row) =>
  row.kind === 'late' ? `${row.minutes} min late` : 'no show'

// What each searchable column shows, for the header search boxes.
const COLUMNS = {
  name: (r) => r.name,
  centre: (r) => r.centre,
  mark: markText,
  late_day: (r) => r.late_day,
  cumulative: (r) => r.cumulative,
  email: (r) => r.email || 'no email',
  manager: (r) => r.manager_email || 'none',
  status: (r) => STATUS_LABEL[r.status]
}

// Alfred's theme stops cells wrapping; let them wrap so the table fits the
// window and the heading can stick to the top of it.
const wrap = { whiteSpace: 'normal', wordBreak: 'break-word' }

const canSend = (row) =>
  row.email && (row.status === 'to_send' || row.status === 'failed')

const errorText = (err) =>
  (err &&
    err.response &&
    err.response.data &&
    (err.response.data.detail || err.response.data.message)) ||
  (err && err.message) ||
  'Something went wrong.'

const PunctualityMails = () => {
  const classes = useStyles()
  const [date, setDate] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [editing, setEditing] = useState(null)
  const poll = useRef(null)

  const load = useCallback(async (day) => {
    try {
      const result = await getPunctualityNotices(day)
      setData(result)
      setDate(result.date || '')
      setError('')
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    return () => clearInterval(poll.current)
  }, [load])

  const act = async (work) => {
    setError('')
    setMessage('')
    try {
      await work()
    } catch (err) {
      setError(errorText(err))
    }
  }

  const send = () =>
    act(async () => {
      setConfirming(false)
      await sendPunctualityDay(date)
      setMessage('Sending. This list updates as each mail goes.')
      clearInterval(poll.current)
      let left = POLL_TIMES
      poll.current = setInterval(() => {
        left -= 1
        if (left <= 0) clearInterval(poll.current)
        load(date)
      }, POLL_MS)
    })

  const toggleRemoved = (row) =>
    act(async () => {
      const result = await clearPunctualityDay({
        date,
        no: row.no,
        undo: row.status === 'cleared'
      })
      if (result.note) setMessage(`Removed. ${result.note}`)
      await load(date)
    })

  const saveEmail = () =>
    act(async () => {
      const row = editing
      setEditing(null)
      const result = await setPunctualityEmail({
        no: row.no,
        email: row.draft
      })
      setMessage(
        result.email
          ? `${row.name}: email saved as ${result.email}.`
          : `${row.name}: email removed, so no mail goes to them.`
      )
      await load(date)
    })

  const rows = (data && data.rows) || []
  const { rows: shown, box, searching } = useColumnSearch(rows, COLUMNS)
  const ready = rows.filter(canSend)
  const missing = rows.filter((r) => !r.email && r.status === 'to_send')
  const blocked = data && data.blocked

  return (
    <div className={classes.root}>
      <div className={classes.bar}>
        <TextField
          inputProps={{ max: data ? data.latest_date : undefined }}
          label="Day"
          InputLabelProps={{ shrink: true }}
          onChange={(event) => {
            setDate(event.target.value)
            if (event.target.value) load(event.target.value)
          }}
          type="date"
          value={date}
        />
        <Button
          color="primary"
          disabled={!ready.length || !!blocked}
          onClick={() => setConfirming(true)}
          variant="contained"
        >
          Send {ready.length} mails
        </Button>
      </div>

      {blocked && <Typography className={classes.note}>{blocked}</Typography>}
      {missing.length > 0 && (
        <Typography className={classes.note}>
          {missing.length} people have no email, so they get no mail.
        </Typography>
      )}
      {message && <Typography className={classes.note}>{message}</Typography>}
      {error && <Typography className={classes.error}>{error}</Typography>}

      {loading ? (
        <CircularProgress />
      ) : (
        <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <SearchHeadCell
                label="Name"
                search={box('name')}
                style={{ width: '15%' }}
              />
              <SearchHeadCell
                label="Centre"
                search={box('centre')}
                style={{ width: '11%' }}
              />
              <SearchHeadCell
                label="Mark"
                search={box('mark')}
                style={{ width: '9%' }}
              />
              <SearchHeadCell
                align="right"
                label="Late day"
                search={box('late_day')}
                style={{ width: 70 }}
              />
              <SearchHeadCell
                align="right"
                label="Deduction"
                search={box('cumulative')}
                style={{ width: 80 }}
              />
              <SearchHeadCell
                label="Email"
                search={box('email')}
                style={{ width: '22%' }}
              />
              <SearchHeadCell
                label="Manager cc"
                search={box('manager')}
                style={{ width: '18%' }}
              />
              <SearchHeadCell
                label="Status"
                search={box('status')}
                style={{ width: '8%' }}
              />
              <SearchHeadCell label="" style={{ width: 90 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((row) => (
              <TableRow
                className={row.status === 'cleared' ? classes.faded : undefined}
                key={row.no}
              >
                <TableCell style={wrap}>{row.name}</TableCell>
                <TableCell style={wrap}>{row.centre || '-'}</TableCell>
                <TableCell style={wrap}>{markText(row)}</TableCell>
                <TableCell align="right">{row.late_day}</TableCell>
                <TableCell align="right">{row.cumulative}</TableCell>
                <TableCell style={wrap}>
                  {row.email || 'no email'}
                  <Button
                    onClick={() => setEditing({ ...row, draft: row.email })}
                    size="small"
                  >
                    Edit
                  </Button>
                </TableCell>
                <TableCell style={wrap}>
                  {row.manager_email || 'none'}
                </TableCell>
                <TableCell style={wrap} title={row.error}>
                  {STATUS_LABEL[row.status]}
                </TableCell>
                <TableCell>
                  {row.status !== 'sending' && (
                    <Button onClick={() => toggleRemoved(row)} size="small">
                      {row.status === 'cleared' ? 'Put back' : 'Remove'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!shown.length && (
              <TableRow>
                <TableCell colSpan={9}>
                  {searching
                    ? 'Nobody matches the search.'
                    : 'Nobody is marked on this day.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog onClose={() => setEditing(null)} open={!!editing}>
        <DialogTitle>Email for {editing && editing.name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            helperText="Saved in Alfred for every day. Leave blank for no mail."
            label="Email"
            onChange={(event) =>
              setEditing({ ...editing, draft: event.target.value })
            }
            type="email"
            value={(editing && editing.draft) || ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button color="primary" onClick={saveEmail} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog onClose={() => setConfirming(false)} open={confirming}>
        <DialogTitle>Send {ready.length} punctuality mails?</DialogTitle>
        <DialogContent>
          These go to staff and their managers for {date}.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button color="primary" onClick={send} variant="contained">
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default PunctualityMails
